import pureDayjs from "dayjs";
import type { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { dayjs } from "@bigbinary/neeto-commons-frontend/utils";

import type { TimeValue } from "../TimePicker/types";

pureDayjs.extend(customParseFormat);

/**
 * Reverse timezone conversion: extract date/time values from a timezone-aware
 * dayjs and create a browser-local Date. This undoes applyTimezone so that
 * Calendar and input display the correct date after a controlled round-trip.
 */
export const toBrowserLocalDate = (date: Date): Date => {
  const d = dayjs(date);

  return new Date(
    d.year(),
    d.month(),
    d.date(),
    d.hour(),
    d.minute(),
    d.second()
  );
};

export const toNativeDate = (value: unknown): Date | null => {
  if (value == null) return null;
  if (value instanceof Date) return value;

  if (typeof (value as { toDate?: () => Date }).toDate === "function") {
    return toBrowserLocalDate((value as { toDate: () => Date }).toDate());
  }

  if (typeof value === "string") {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) return toBrowserLocalDate(parsed);
  }

  return null;
};

export const coerceDateValue = (
  value: unknown,
  type: "date" | "range"
): Date | [Date | null, Date | null] | null => {
  if (value == null) return null;
  if (type === "range" && Array.isArray(value)) {
    return [toNativeDate(value[0]), toNativeDate(value[1])];
  }

  return toNativeDate(value);
};

export const getInitialCalendarMonth = (
  value: unknown,
  defaultValue: unknown,
  type: "date" | "range"
): Date => {
  const initial = coerceDateValue(value ?? defaultValue, type);
  if (initial instanceof Date) return initial;
  if (Array.isArray(initial) && initial[0] instanceof Date) return initial[0];

  return new Date();
};

const normalizeToDayjsFormat = (fmt: string): string =>
  fmt
    .replace(/\byyyy\b/g, "YYYY")
    .replace(/\byy\b/g, "YY")
    .replace(/\bdd\b/g, "DD")
    .replace(/\bd\b/g, "D");

const normalizeToDateFnsFormat = (fmt: string): string =>
  fmt
    .replace(/YYYY/g, "yyyy")
    .replace(/YY/g, "yy")
    .replace(/DD/g, "dd")
    .replace(/\bD\b/g, "d");

/**
 * Format a date for display using browser-local time.
 * Calendar and input always show browser-local dates.
 */
export const formatDate = (date: Date | null, formatStr: string): string => {
  if (!date) return "";

  try {
    return pureDayjs(date).format(normalizeToDayjsFormat(formatStr));
  } catch {
    return "";
  }
};

/**
 * Parse a date string using browser-local pureDayjs (not timezone-aware).
 * The user types in browser-local time; timezone conversion happens
 * later in commitValue via applyTimezone.
 */
export const parseDate = (str: string, formatStr: string): Date | null => {
  if (!str) return null;

  const dayjsFmt = normalizeToDayjsFormat(formatStr);
  const parsed = pureDayjs(str, dayjsFmt);
  if (parsed.isValid()) return parsed.toDate();

  const fallbackFormats = [
    "DD/MM/YYYY",
    "MM/DD/YYYY",
    "YYYY-MM-DD",
    "DD-MM-YYYY",
    "DD/MM/YYYY HH:mm:ss",
    "DD/MM/YYYY HH:mm",
  ];

  for (const fmt of fallbackFormats) {
    if (fmt === dayjsFmt) continue;

    const attempt = pureDayjs(str, fmt);
    if (attempt.isValid()) return attempt.toDate();
  }

  return null;
};

export const applyTimeToDate = (date: Date, time: TimeValue): Date => {
  const result = new Date(date);
  result.setHours(time.hours, time.minutes, time.seconds ?? 0, 0);

  return result;
};

/**
 * Reinterpret a native Date's browser-local time values in the user's configured timezone.
 * Formats in browser-local time (pureDayjs), then reparses through timezone-aware dayjs.
 * Matches neetoUI's getTimezoneAppliedDateTime behavior.
 */
export const applyTimezone = (date: Date): Date => {
  const dateStr = pureDayjs(date).format("YYYY-MM-DD HH:mm:ss");

  return dayjs(dateStr).toDate();
};

export const toUserTzDayjs = (date: Date): Dayjs =>
  dayjs(pureDayjs(date).format("YYYY-MM-DD HH:mm:ss"));

export const toDayjs = (date: Date): Dayjs => dayjs(date);

export const getDisplayFormat = (
  dateFormat: string,
  timeFormat: string,
  showTime: boolean
): string => {
  const fmt = showTime ? `${dateFormat} ${timeFormat}` : dateFormat;

  return normalizeToDateFnsFormat(fmt);
};

export const getDatePlaceholder = (
  dateFormat: string,
  timeFormat: string,
  showTime: boolean,
  type: "date" | "range"
): string => {
  const single = showTime
    ? `${dateFormat.toUpperCase()} ${timeFormat}`
    : dateFormat.toUpperCase();

  return type === "range" ? `${single} - ${single}` : single;
};

export const isDatePartComplete = (
  part: string,
  maskEnabled: boolean,
  singleDateLen: number
): boolean => (maskEnabled ? part.length >= singleDateLen : part.length > 0);

export const parseRangeText = (
  text: string,
  displayFormat: string,
  maskEnabled: boolean,
  singleDateLen: number
): [Date, Date] | null => {
  const parts = text.split(" - ");
  if (parts.length !== 2) return null;

  if (
    !isDatePartComplete(parts[0], maskEnabled, singleDateLen) ||
    !isDatePartComplete(parts[1], maskEnabled, singleDateLen)
  )
    return null;

  const from = parseDate(parts[0], displayFormat);
  const to = parseDate(parts[1], displayFormat);

  return from && to ? [from, to] : null;
};

export const startOfWeek = (date: Date, weekStartsOn = 0): Date => {
  const d = new Date(date);
  const diff = (d.getDay() - weekStartsOn + 7) % 7;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);

  return d;
};

export const weekDaysFrom = (start: Date): Date[] =>
  Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);

    return d;
  });

export const isMonthDisabled = (
  monthDate: Date,
  minDate?: Date,
  maxDate?: Date
): boolean => {
  const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const monthEnd = new Date(
    monthDate.getFullYear(),
    monthDate.getMonth() + 1,
    0,
    23,
    59,
    59,
    999
  );
  if (maxDate && monthStart > maxDate) return true;
  if (minDate && monthEnd < minDate) return true;

  return false;
};

export const isYearDisabled = (
  yearDate: Date,
  minDate?: Date,
  maxDate?: Date
): boolean => {
  const yearStart = new Date(yearDate.getFullYear(), 0, 1);
  const yearEnd = new Date(yearDate.getFullYear(), 11, 31, 23, 59, 59, 999);
  if (maxDate && yearStart > maxDate) return true;
  if (minDate && yearEnd < minDate) return true;

  return false;
};

export const buildMonth = (year: number, month: number): Date =>
  new Date(year, month, 1);

export const isSameMonth = (a: Date | null, b: Date): boolean =>
  !!a && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

export const isSameDay = (a: Date | null, b: Date | null): boolean =>
  !!a &&
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const orderRange = (
  from: Date | null,
  to: Date | null
): [Date | null, Date | null] => {
  if (from && to && from.getTime() > to.getTime()) return [to, from];

  return [from, to];
};

export const parseGridcellDay = (target: EventTarget | null): Date | null => {
  const el = target as HTMLElement | null;
  const cell = el?.closest('[role="gridcell"][data-day]');
  const dayAttr = cell?.getAttribute("data-day");
  if (!dayAttr) return null;

  // react-day-picker emits "YYYY-MM-DD". `new Date("YYYY-MM-DD")` parses as UTC
  // midnight, which shifts the date by the local TZ offset (e.g. shows as 05:30
  // AM in IST). Parse the parts directly to get local midnight.
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dayAttr);
  if (match) return new Date(+match[1], +match[2] - 1, +match[3]);

  const parsed = new Date(dayAttr);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const computeRangePreview = ({
  pendingRange,
  committed,
  hoveredDate,
  rangeStep,
}: {
  pendingRange: [Date | null, Date | null] | null;
  committed: [Date | null, Date | null] | null;
  hoveredDate: Date | null;
  rangeStep: "from" | "to";
}): { from: Date | null; to: Date | null } => {
  let from = pendingRange ? pendingRange[0] : (committed?.[0] ?? null);
  let to = pendingRange ? pendingRange[1] : (committed?.[1] ?? null);

  if (hoveredDate) {
    if (rangeStep === "from" && !pendingRange?.[0]) {
      from = hoveredDate;
      if (to && hoveredDate.getTime() > to.getTime()) to = null;
    } else if (
      rangeStep === "to" &&
      !pendingRange?.[1] &&
      !isSameDay(from, hoveredDate)
    ) {
      to = hoveredDate;
      if (from && hoveredDate.getTime() < from.getTime()) from = null;
    }
  }

  [from, to] = orderRange(from, to);

  return { from, to };
};

export const decadeStartFor = (year: number): number =>
  Math.floor(year / 10) * 10;
